import { useState } from 'react';
import clsx from 'clsx';

import { DecisionSelector } from '../../components';
import type { WizardPersona, PersonaDecisions } from '../../types';
import { DECISION_DIMENSIONS, defaultDecisions } from '../../types';

export type PersonalityStepProps = {
  persona: Partial<WizardPersona>;
  onChange: (updates: Partial<WizardPersona>) => void;
};

// Group decisions into logical categories
const DECISION_GROUPS: Array<{
  id: string;
  title: string;
  description: string;
  dimensions: Array<keyof PersonaDecisions>;
}> = [
  {
    id: 'framing',
    title: 'Framing',
    description: 'How ideas are presented',
    dimensions: ['claimOwnership', 'confidence', 'specificity'],
  },
  {
    id: 'engagement',
    title: 'Engagement',
    description: 'How the persona connects',
    dimensions: ['callToAction', 'vulnerability', 'energy'],
  },
  {
    id: 'style',
    title: 'Style',
    description: 'How the persona sounds',
    dimensions: ['formality'],
  },
];

export function PersonalityStep({ persona, onChange }: PersonalityStepProps) {
  const [activeGroup, setActiveGroup] = useState(0);
  const decisions = persona.decisions || defaultDecisions;

  const updateDecision = (key: keyof PersonaDecisions, value: string) => {
    onChange({
      decisions: {
        ...decisions,
        [key]: value,
      },
    });
  };

  const currentGroup = DECISION_GROUPS[activeGroup];
  const dimensionsInGroup = DECISION_DIMENSIONS.filter((d) =>
    currentGroup.dimensions.includes(d.key)
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Decisions</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-dark-400">
          Configure how this persona frames and expresses meaning
        </p>
      </div>

      {/* Group tabs */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-dark-600">
        {DECISION_GROUPS.map((group, index) => (
          <button
            key={group.id}
            type="button"
            onClick={() => setActiveGroup(index)}
            className={clsx(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              activeGroup === index
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-dark-400 dark:hover:text-dark-200'
            )}
          >
            {group.title}
          </button>
        ))}
      </div>

      {/* Group description */}
      <div className="p-3 bg-gray-50 dark:bg-dark-700 rounded-lg">
        <p className="text-sm text-gray-600 dark:text-dark-300">{currentGroup.description}</p>
      </div>

      {/* Decision selectors for current group */}
      <div className="space-y-8">
        {dimensionsInGroup.map((dimension) => (
          <DecisionSelector
            key={dimension.key}
            dimension={dimension}
            value={decisions[dimension.key]}
            onChange={updateDecision}
          />
        ))}
      </div>

      {/* Navigation hint */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-dark-600">
        <button
          type="button"
          onClick={() => setActiveGroup(Math.max(0, activeGroup - 1))}
          disabled={activeGroup === 0}
          className={clsx(
            'text-sm font-medium transition-colors',
            activeGroup === 0
              ? 'text-gray-300 dark:text-dark-600 cursor-not-allowed'
              : 'text-primary-600 hover:text-primary-700 dark:text-primary-400'
          )}
        >
          &larr; {activeGroup > 0 ? DECISION_GROUPS[activeGroup - 1].title : ''}
        </button>

        <div className="flex gap-1">
          {DECISION_GROUPS.map((_, index) => (
            <div
              key={index}
              className={clsx(
                'w-2 h-2 rounded-full transition-colors',
                index === activeGroup ? 'bg-primary-500' : 'bg-gray-300 dark:bg-dark-600'
              )}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={() => setActiveGroup(Math.min(DECISION_GROUPS.length - 1, activeGroup + 1))}
          disabled={activeGroup === DECISION_GROUPS.length - 1}
          className={clsx(
            'text-sm font-medium transition-colors',
            activeGroup === DECISION_GROUPS.length - 1
              ? 'text-gray-300 dark:text-dark-600 cursor-not-allowed'
              : 'text-primary-600 hover:text-primary-700 dark:text-primary-400'
          )}
        >
          {activeGroup < DECISION_GROUPS.length - 1 ? DECISION_GROUPS[activeGroup + 1].title : ''}{' '}
          &rarr;
        </button>
      </div>

      {/* Current settings summary */}
      <div className="p-4 bg-gray-50 dark:bg-dark-700 rounded-lg">
        <h4 className="text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider mb-3">
          Current Settings
        </h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
          {DECISION_DIMENSIONS.map((dim) => {
            const level = dim.levels.find((l) => l.value === decisions[dim.key]);
            return (
              <div key={dim.key} className="flex flex-col">
                <span className="text-gray-500 dark:text-dark-400 text-xs">{dim.key}</span>
                <span className="text-gray-900 dark:text-white font-medium truncate">
                  {level?.label || decisions[dim.key]}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
